import { useLocalSearchParams } from 'expo-router'
import { ChevronRight, MapPin, User } from 'lucide-react-native'
import { Dimensions, StyleSheet, View } from 'react-native'
import { Text as PaperText, useTheme } from 'react-native-paper'
import { formatAddress, isAddressBlank } from '@/lib/address'
import { BREAKPOINTS } from '@/lib/config'
import type { Job, JobStatus, JobWithRelations } from '@/types/jobs'
import type { Tag as TagModel } from '@/types/tags'
import { CardLink } from '@/ui/CardLink'
import { JobStatusBadge } from '@/ui/jobs/JobStatusBadge'
import { Tag } from '@/ui/tags/Tag'

const isSmallScreen = Dimensions.get('window').width < BREAKPOINTS.sm

interface JobCardProps {
    job: Job | JobWithRelations
    tags?: TagModel[]
    onChangeStatus?: (jobId: string, status: JobStatus) => void
}

export function JobCard({ job, tags, onChangeStatus }: JobCardProps) {
    const params = useLocalSearchParams<{ orgSlug: string }>()
    const theme = useTheme()

    return (
        <CardLink href={`/${params.orgSlug}/settings/jobs/${job.identifier}`} minHeight={85}>
            <View style={styles.cardContent}>
                <JobInfo job={job} tags={tags} />
                <JobStatusControl job={job} onChangeStatus={onChangeStatus} />
                <ChevronRight
                    size={20}
                    color={theme.colors.onSurfaceVariant}
                    style={styles.chevron}
                />
            </View>
        </CardLink>
    )
}

function JobInfo({ job, tags }: { job: Job | JobWithRelations; tags?: TagModel[] }) {
    const hasAddress = 'expand' in job && job.expand?.address && !isAddressBlank(job.expand.address)
    const hasCustomer = 'expand' in job && job.expand?.customer
    const hasTags = Boolean(tags && tags.length > 0)
    const createdDate = new Date(job.created).toLocaleDateString()
    const theme = useTheme()

    return (
        <View style={styles.jobInfo}>
            <PaperText
                variant={isSmallScreen ? 'titleMedium' : 'titleLarge'}
                style={[styles.titleText, { color: theme.colors.onSurface }]}
                numberOfLines={2}
                ellipsizeMode="tail"
            >
                {job.name || job.identifier}
            </PaperText>

            <View style={styles.dateRow}>
                <PaperText
                    variant="bodySmall"
                    style={{ color: theme.colors.onSurfaceVariant, fontSize: 12 }}
                >
                    {createdDate}
                </PaperText>
                {hasTags &&
                    tags?.map(tag => (
                        <Tag
                            key={tag.id}
                            tag={tag}
                            variant="filled"
                            compact
                            style={styles.tagChip}
                        />
                    ))}
            </View>

            {hasCustomer && (
                <View style={styles.jobDetail}>
                    <User size={12} color={theme.colors.onSurfaceVariant} />
                    <PaperText
                        variant="bodySmall"
                        style={{ color: theme.colors.onSurfaceVariant, flex: 1, fontSize: 13 }}
                        numberOfLines={1}
                    >
                        {(job as JobWithRelations).expand?.customer?.name}
                    </PaperText>
                </View>
            )}

            {hasAddress && (
                <View style={styles.jobDetail}>
                    <MapPin size={12} color={theme.colors.onSurfaceVariant} />
                    <PaperText
                        variant="bodySmall"
                        style={{ color: theme.colors.onSurfaceVariant, flex: 1, fontSize: 13 }}
                        numberOfLines={1}
                    >
                        {formatAddress((job as JobWithRelations).expand?.address)}
                    </PaperText>
                </View>
            )}
        </View>
    )
}

function JobStatusControl({
    job,
    onChangeStatus,
}: {
    job: Job | JobWithRelations
    onChangeStatus?: (jobId: string, status: JobStatus) => void
}) {
    const changeStatus = (status: JobStatus) => onChangeStatus?.(job.id, status)
    return <JobStatusBadge job={job} onChangeStatus={changeStatus} style={styles.statusControl} />
}

const styles = StyleSheet.create({
    cardContent: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 12,
    },
    jobInfo: {
        flex: 1,
        gap: 6,
        minWidth: 0,
    },
    jobDetail: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        marginTop: 2,
    },
    chevron: {
        marginLeft: 4,
        alignSelf: 'center',
        flexShrink: 0,
    },
    statusControl: {
        alignSelf: 'center',
        flexShrink: 0,
    },
    titleText: {
        fontWeight: '600',
        lineHeight: 24,
        minWidth: 0,
    },
    dateRow: {
        flexDirection: 'row',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 6,
    },
    tagChip: {
        marginRight: 0,
        marginLeft: 0,
        marginBottom: 0,
    },
})
